// =============================================
// resetSkipStatus — UI helper żeby user mógł odblokować/zablokować nod
// zostawiony jako `skipped_existing` przez wp_matching audit.
//
// Scenariusze:
// 1. Audit wp_matching dopasował nod do istniejącego (słabego) WP postu
//    → user chce REGENEROWAĆ od zera bo treść słaba
// 2. Audit wp_matching wątpił (partial) → user świadomie decyduje skip
// 3. User regret — zmienił zdanie i chce zostawić istniejący wpis w spokoju
// =============================================
import { supabase } from "@/integrations/supabase/client";
import { logPublic, logError } from "./contentLogger";

const PREFIX = "[RESET_SKIP]";

export type ResetMode =
  | "regenerate"   // pozwól pipeline generować od zera (wymuś nowy artykuł)
  | "skip_manual"; // user świadomie zostawia skip (locks na decyzję)

interface ResetResult {
  success: boolean;
  message: string;
  newMatchStatus?: string;
  newEngineStage?: string | null;
}

/**
 * Reset skip status nodu. Zmienia thematic_nodes.match_status i
 * thematic_node_content.engine_stage tak żeby pipeline mógł (lub nie)
 * generować content w następnym batchu.
 *
 * REGENERATE:
 *  - match_status: matched_keep/partial/matched_refurbish → unmatched
 *  - engine_stage: skipped_existing → null (pipeline pickuje przy next batch)
 *  - engine_debug: dodaje audyt log {unskipped_at, unskipped_by, prev_skip_reason}
 *
 * SKIP_MANUAL:
 *  - match_status: cokolwiek → skip_manual
 *  - engine_stage: zostaje skipped_existing (lub set jeśli był null)
 *  - engine_debug: dodaje {locked_at, locked_by_user: true}
 */
export async function resetNodeSkipStatus(
  nodeId: string,
  mode: ResetMode,
): Promise<ResetResult> {
  try {
    // 1. Load current state (debug log + audit trail)
    // INCLUDE refurbish_approved_by_user + matched_wp_post_id żeby móc je clear przy regenerate
    // (bug 2026-05-19: orphan flag refurbish_approved_by_user=true + matched_wp_post_id=X +
    //  wp_posts.final_action='refurbish' powoduje publish failure z błędem
    //  "match_status='unmatched' (oczekiwano 'matched_refurbish')")
    const { data: nodeData, error: nodeErr } = await (supabase as any)
      .from("thematic_nodes")
      .select("id, match_status, user_id, map_id, refurbish_approved_by_user, matched_wp_post_id")
      .eq("id", nodeId)
      .maybeSingle();

    if (nodeErr || !nodeData) {
      return { success: false, message: `Nie znalazłem nodu ${nodeId.slice(0, 8)}` };
    }

    const node = nodeData as {
      id: string;
      match_status: string | null;
      user_id: string;
      map_id: string;
      refurbish_approved_by_user: boolean | null;
      matched_wp_post_id: string | null;
    };

    const { data: contentData } = await supabase
      .from("thematic_node_content")
      .select("id, engine_stage, engine_debug")
      .eq("node_id", nodeId)
      .maybeSingle();

    const content = contentData as {
      id: string;
      engine_stage: string | null;
      engine_debug: Record<string, unknown> | null;
    } | null;

    const prevDebug = (content?.engine_debug ?? {}) as Record<string, unknown>;
    const prevSkipReason = prevDebug?.skip_reason;

    if (mode === "regenerate") {
      // Update node — clear ALL refurbish-related flags żeby unikać orphan state.
      // BEZ tego: jeśli wp_matching kiedyś matchnął nod do WP postu (np. final_action='refurbish'),
      // publish_queue bierze refurbish path i fail'uje z błędem "expected matched_refurbish".
      const { error: nodeUpdErr } = await (supabase as any)
        .from("thematic_nodes")
        .update({
          match_status: "unmatched",
          refurbish_approved_by_user: false,
          matched_wp_post_id: null,
        })
        .eq("id", nodeId);

      if (nodeUpdErr) {
        return { success: false, message: `DB error nodes: ${nodeUpdErr.message}` };
      }

      // Jeśli node miał mapping do wp_posts, wyczyść też final_action żeby M15 Decision Engine
      // nie kierował publish_queue na refurbish path. ZACHOWUJEMY wp_posts row (artykuł WP nadal żyje
      // pod swoim URL), tylko zrywamy decyzję M15.
      let prevWpFinalAction: string | null = null;
      if (node.matched_wp_post_id) {
        const { data: wpData } = await (supabase as any)
          .from("wp_posts")
          .select("final_action")
          .eq("id", node.matched_wp_post_id)
          .maybeSingle();
        prevWpFinalAction = (wpData as { final_action: string | null } | null)?.final_action ?? null;

        const { error: wpUpdErr } = await (supabase as any)
          .from("wp_posts")
          .update({ final_action: null, final_action_locked: false })
          .eq("id", node.matched_wp_post_id);

        if (wpUpdErr) {
          // Nie blokujemy — log warning, kontynuuj. wp_posts cleanup to nice-to-have.
          logError(PREFIX, `wp_posts cleanup failed (non-blocking): ${wpUpdErr.message}`);
        }
      }

      // Update content engine_stage → null + pełen audit trail
      if (content) {
        const newDebug = {
          ...prevDebug,
          unskipped_at: new Date().toISOString(),
          unskipped_via_ui: true,
          prev_skip_reason: prevSkipReason,
          prev_match_status: node.match_status,
          prev_refurbish_approved: node.refurbish_approved_by_user,
          prev_matched_wp_post_id: node.matched_wp_post_id,
          prev_wp_final_action: prevWpFinalAction,
        };
        const { error: contentUpdErr } = await (supabase as any)
          .from("thematic_node_content")
          .update({ engine_stage: null, engine_debug: newDebug })
          .eq("node_id", nodeId);

        if (contentUpdErr) {
          return { success: false, message: `DB error content: ${contentUpdErr.message}` };
        }
      }

      const wpCleanupNote = node.matched_wp_post_id
        ? ` + wp_posts ${node.matched_wp_post_id.slice(0, 8)} final_action cleared (was: ${prevWpFinalAction ?? "null"})`
        : "";
      logPublic(PREFIX, `Node ${nodeId.slice(0, 8)} → unskipped (was: ${node.match_status}/${content?.engine_stage ?? "null"})${wpCleanupNote}`);
      return {
        success: true,
        message: "Nod odblokowany — pipeline wygeneruje go w następnym batchu, publish utworzy nowy WP post",
        newMatchStatus: "unmatched",
        newEngineStage: null,
      };
    }

    if (mode === "skip_manual") {
      // Update node → skip_manual
      const { error: nodeUpdErr } = await supabase
        .from("thematic_nodes")
        .update({ match_status: "skip_manual" })
        .eq("id", nodeId);

      if (nodeUpdErr) {
        return { success: false, message: `DB error nodes: ${nodeUpdErr.message}` };
      }

      // Update content engine_stage → skipped_existing (ensure consistent state)
      const newDebug = {
        ...prevDebug,
        skip_reason: "skip_manual",
        locked_at: new Date().toISOString(),
        locked_by_user: true,
      };

      if (content) {
        await supabase
          .from("thematic_node_content")
          .update({ engine_stage: "skipped_existing", engine_debug: newDebug })
          .eq("node_id", nodeId);
      } else {
        // Create stub content row z skip metadata
        await supabase.from("thematic_node_content").insert({
          node_id: nodeId,
          user_id: node.user_id,
          map_id: node.map_id,
          engine_stage: "skipped_existing",
          engine_debug: newDebug,
        });
      }

      logPublic(PREFIX, `Node ${nodeId.slice(0, 8)} → locked as skip_manual`);
      return {
        success: true,
        message: "Nod zablokowany jako skip ręczny — pipeline NIE będzie go generował",
        newMatchStatus: "skip_manual",
        newEngineStage: "skipped_existing",
      };
    }

    return { success: false, message: `Nieznany mode: ${mode}` };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "unknown error";
    logError(PREFIX, `resetNodeSkipStatus failed: ${msg}`);
    return { success: false, message: `Błąd: ${msg}` };
  }
}
